<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Operações Planejadas
    </div>

    <v-card>
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="harvestId"
              label="Safra"
              :items="harvestList"
              item-value="id"
              item-text="name"
              @change="loadCulturesFromHarvest"
              clearable
              @click:clear="harvestId = null; cultureList = []; cultureId = null"
              :loading="harvestListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="cultureId"
              label="Cultura"
              :items="harvestId ? cultureList : allCultureList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="cultureId = null"
              @change="loadPeriodsFromHarvestAndCulture"
              :loading="cultureListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="periodIds"
              label="Ciclo(s)"
              :items="harvestId ? periodList : allPeriodList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="periodIds = []"
              :loading="periodListLoading"
              multiple
              chips
              dense
              deletable-chips
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeSelectedPeriod(data.item)"
                >
                  {{ data.item.name }} 
                  <small class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.additionalInfo}}) </small>
                  <small v-if="data.item.secondAdditionalInfo" class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.secondAdditionalInfo}}) </small>
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-subtitle-1">{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{data.item.additionalInfo}}
                    <br v-if="data.item.secondAdditionalInfo">
                    {{data.item.secondAdditionalInfo}}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="fieldIds"
              label="Talhão(ões)"
              :items="fieldsList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="fieldIds = []"
              :loading="harvestListLoading"
              multiple
              chips
              dense
              deletable-chips
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="operationTypeIds"
              label="Tipo(s) de Operação(ões)"
              :items="operationTypeList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="operationTypeIds = []"
              :loading="harvestListLoading"
              multiple
              chips
              dense
              deletable-chips
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="periodFilterModel"
              label="Período"
              :items="periodFilterOptions"
              item-value="key"
              item-text="name"
            />
          </v-col>
          <v-col cols="12" sm="4" v-if="periodFilterModel == 'OTHER'">
            <DateRangeField
              :inputData.sync="periodFilterRangeModel"
              label="Período Customizado"
              :hasTodayDate="true"
            />
          </v-col>

          <v-col cols="12" sm="2">
            <v-btn 
              @click="loadPlanningOperationReport"
              :disabled="false"
              class="mt-2"
            >
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="planningOperationReportTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Operações Planejadas"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        v-if="planningOperationsReport"
        class="mt-3"
      />
    </div>

    <div id="planningOperationReportTable">
      <v-simple-table 
        v-if="planningOperationsReport"
        class="mt-3"
      >
        <template v-slot:default>

          <v-timeline
            align-top
            dense
          >
            <v-timeline-item
              v-for="(planningOperation, i) in planningOperationsReport"
              :key="i + '_operation_group'"
              :color="operationTypeColor(planningOperation.operationType)"
              :icon="operationTypeIcon(planningOperation.operationType)"
              fill-dot
              :small="$vuetify.breakpoint.xsOnly"
            >
              <v-card
                :color="operationTypeColor(planningOperation.operationType)"
              >
                <v-card-title class="title">
                  {{planningOperation.operationDate | formatDate}} - {{planningOperation.operationTypeText}} - {{planningOperation.culture}}
                </v-card-title>
                <v-card-text class="white text--primary">

                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Talhão(ões)</th>
                          <th class="text-left">Safra</th>
                          <th class="text-left">Área</th>
                          <th class="text-left">Funcionário</th>
                          <th class="text-left">Patrimônio</th>
                          <th class="text-left">Implemento</th>
                          <th class="text-left">Duração</th>
                          <th class="text-left" v-if="planningOperation.operationType != 'soil_preparation' && planningOperation.operationType != 'crop'">Itens Consumidos</th>
                          <th class="text-left" v-if="planningOperation.operationType != 'soil_preparation' && planningOperation.operationType != 'crop'">Dose/ha</th>
                          <th class="text-left" v-if="planningOperation.operationType == 'crop'">Produtos Produzidos</th>
                          <th class="text-left" v-if="planningOperation.operationType == 'crop'">Qtd.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="operation in planningOperation.operations"
                          :key="operation.operationId + 'operationLine'"
                        >
                          <td>
                            <div v-for="(period, i) in operation.periods" :key="operation.operationId + period + i">
                              {{ period }}
                            </div>
                          </td>
                          <td>{{ operation.harvest }}</td>
                          <td>{{ operation.totalArea | formatNumber }} ha</td>
                          <td colspan="4" style="padding-left: 0; padding-right: 0;">
                            <v-simple-table>
                              <template v-slot:default>
                                <tbody>
                                  <tr v-for="activity in operation.activities" :key="activity.activityId">
                                    <td> {{ activity.employee }} </td>
                                    <td> {{ activity.patrimony ? activity.patrimony : '-' }} </td>
                                    <td> {{ activity.implement ? activity.implement : '-' }} </td>
                                    <td> {{ activity.duration }} </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </td>
                          
                          <td colspan="2" v-if="planningOperation.operationType != 'soil_preparation'">
                            <v-simple-table>
                              <template v-slot:default>
                                <tbody v-if="planningOperation.operationType != 'crop'">
                                  <tr v-for="dummyExitMovementItem in operation.dummyExitMovementItems" :key="dummyExitMovementItem.dummyExitMovementItemId">
                                    <td> {{ dummyExitMovementItem.item }} </td>
                                    <td style="min-width: 130px;"> {{ dummyExitMovementItem.dose }} </td>
                                  </tr>
                                </tbody>
                                <tbody v-else>
                                  <tr v-for="dummyEntryMovementItem in operation.dummyEntryMovementItems" :key="dummyEntryMovementItem.dummyEntryMovementItemId">
                                      <td> {{ dummyEntryMovementItem.item }} </td>
                                      <td style="min-width: 130px;"> {{ dummyEntryMovementItem.quantity }} </td>
                                    </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>

        </template>
      </v-simple-table>
    </div>
    
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";

export default {
  data() {
    return {
      tableItems: [],
      planningOperationsReport: null,
      loading: false,
      harvestListLoading: false,
      harvestList: [],
      harvestId: null,
      cultureList: [],
      allCultureList: [],
      cultureListLoading: false,
      cultureId: null,
      periodListLoading: false,
      periodList: [],
      allPeriodList: [],
      operationTypeList: [],
      periodIds: [],
      operationTypeIds: [],
      fieldsList: [],
      fieldIds: [],
      startDate: null,
      endDate: null,
      monthFilter: moment().format('YYYY-MM'),
      periodFilterOptions: [
        { "key": null, "name": "Sem filtro de data" },
        { "key": "DAY", "name": "Hoje" },
        { "key": "WEEK", "name": "Esta Semana" },
        { "key": "MONTH", "name": "Este Mês" },
        { "key": "OTHER", "name": "Customizado" },
      ],
      periodFilterModel: "MONTH",
      periodFilterRangeModel: [moment().startOf('week').format('YYYY-MM-DD'), moment().endOf('week').format('YYYY-MM-DD')]
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
  },
  created() {
    this.loadAllHarvests();
  },
  mounted() { 
  },
  computed: {
    tableHeaders: function () { }
  },
  methods: {
    loadPlanningOperationReport: function() {
      this.loading = true;
      this.runDateFilter();
      this.$apollo
        .query({
          query: LOAD_PLANNING_OPERATIONS_REPORT,
          variables: {
            harvestId: this.harvestId, 
            cultureId: this.cultureId, 
            periodIds: this.periodIds,
            fieldIds: this.fieldIds,
            operationTypeIds: this.operationTypeIds, 
            startDate: this.startDate, 
            endDate: this.endDate
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.planningOperationsReport = response.data.accountContext.planningOperationsReport;
          // console.log("planningOperationsReport", this.planningOperationsReport);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    loadAllHarvests: function() {
        this.harvestListLoading = true;
        this.$apollo
          .query({
            query: LOAD_ALL_HARVEST_QUERY,
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.harvestList = response.data.accountContext.allHarvests;
            this.operationTypeList = response.data.accountGlobalSharedContext.allOperationTypes;
            this.fieldsList = response.data.groundContext.allFields;
            this.allCultureList = response.data.accountContext.allCulturesForAccount;
            this.allPeriodList = response.data.accountContext.allPeriods;
          })
          .catch(response => console.log(response))
          .finally(() => (this.harvestListLoading = false));
    },
    loadCulturesFromHarvest: function() {
      if (this.harvestId){
        this.cultureListLoading = true;
        this.$apollo
          .query({
            query: CULTURES_FROM_HARVEST_QUERY,
            variables: {
              harvestId: this.harvestId
            },
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.cultureList = response.data.accountContext.allCulturesFromHarvest;
            this.cultureId = null;
            this.periodIds = [];
          })
          .catch(response => console.log(response))
          .finally(() => (this.cultureListLoading = false));
        
        this.loadPeriodsFromHarvestAndCulture();
      }
    },
    loadPeriodsFromHarvestAndCulture: function() {
      this.periodListLoading = true;
      this.$apollo
        .query({
          query: PERIODS_FROM_HARVEST_AND_CULTURES_QUERY,
          variables: {
            harvestId: this.harvestId,
            cultureId: this.cultureId
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.periodList = response.data.accountContext.allPeriods;
          this.periodIds = [];
        })
        .catch(response => console.log(response))
        .finally(() => (this.periodListLoading = false));
    },
    removeSelectedPeriod: function(item) {
      const index = this.periodIds.indexOf(item.id)
      if (index >= 0) this.periodIds.splice(index, 1)
    },
    operationTypeColor: function(operationType) {
      switch(operationType) {
        case "soil_preparation":
          return "#8D6E63"; //brown
        case "planting":
          return "#66BB6A"; //green
        case "forage_planting":
          return "#7CB342"; //light-green
        case "fertilization":
          return "#FFA726"; //orange
        case "application":
          return "#FF7043"; //deep-orange
        case "crop":
          return "#29B6F6"; //light-blue
        default:
          return "#78909C"; //blue-grey
      }
    },
    operationTypeIcon: function(operationType) {
      switch(operationType) {
        case "soil_preparation":
          return "mdi-tractor";
        case "planting":
          return "mdi-seed";
        case "forage_planting":
          return "mdi-barley";
        case "fertilization":
          return "mdi-sprinkler"; 
        case "application":
          return "mdi-spray";
        case "crop":
          return "mdi-sprout";
        default:
          return "mdi-leaf";
      }
    },
    runDateFilter: function() {
      if (this.periodFilterModel == null) {
        this.startDate = null;
        this.endDate   = null;
      } else if (this.periodFilterModel != "OTHER") {
        this.startDate = moment().startOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
        this.endDate   = moment().endOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
      } else {
        if (this.periodFilterRangeModel.length == 2) {
          this.startDate = this.periodFilterRangeModel[0];
          this.endDate   = this.periodFilterRangeModel[1];
        }
      }
      // console.log(this.startDate, this.endDate);
      // this.loadExecutionOperationReport();
      // this.startDate = moment(this.monthFilter, 'YYYY-MM').startOf('week').format('YYYY-MM-DD');
      // this.endDate   = moment(this.monthFilter, 'YYYY-MM').endOf('week').format('YYYY-MM-DD');
    },
  }
};



const LOAD_PLANNING_OPERATIONS_REPORT = gql `
query ($harvestId: ID, $cultureId: ID, $periodIds: [ID!], $operationTypeIds: [ID!], $fieldIds: [ID!], $startDate: ISO8601Date, $endDate: ISO8601Date) {
  accountContext {
    planningOperationsReport(harvestId: $harvestId, cultureId: $cultureId, periodIds: $periodIds, operationTypeIds: $operationTypeIds, fieldIds: $fieldIds, startDate: $startDate, endDate: $endDate) 
  }
}
`;

const LOAD_ALL_HARVEST_QUERY = gql `
  {
    accountContext {
      allHarvests {
        id
        name
      }
      allCulturesForAccount {
        id
        name
      }
      allPeriods {
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
    accountGlobalSharedContext {
      allOperationTypes {
        id
        name
      }
    }
    groundContext {
      allFields {
        id
        name
      }
    }
  }
`;

const CULTURES_FROM_HARVEST_QUERY = gql `
  query ($harvestId: ID!) {
    accountContext {
      allCulturesFromHarvest(harvestId: $harvestId){
        id
        name
      }
    }
  }
`;

const PERIODS_FROM_HARVEST_AND_CULTURES_QUERY = gql `
  query ($harvestId: ID, $cultureId: ID) {
    accountContext {
      allPeriods(harvestId: $harvestId, cultureId: $cultureId){
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
  }
`;

</script>
<style scoped>
</style>
